import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Smartphone, Monitor, Tablet as TabletIcon, Tv2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MetricsColumnFilter, MetricColumn } from '../shared/MetricsColumnFilter';
import { PitchSelector } from '../pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '../pitch/pitchSelection';
import type { VideoPitch } from '@/types/pitch';
import '../metrics-table.css';

interface DeviceMetrics {
  device: 'mobile' | 'desktop' | 'tablet' | 'tv' | 'other';
  visualizations: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  playRate: number;
  buttonClicks: number;
  engagement: number;
  /** Retenção (%) no pitch escolhido no seletor. null = sem pitch ou sem dado. */
  pitchRetention: number | null;
  conversions: number;
  revenue: number;
}

interface DevicesMetricsTableProps {
  data?: any[];
  /** `video.pitches` do consolidado — opções do seletor e índice em `pitchesRetentionPct`. */
  pitches?: VideoPitch[];
}

function getDeviceLabel(device: string) {
  switch (device.toLowerCase()) {
    case 'mobile':
    case 'celular':
      return 'Mobile';
    case 'desktop':
    case 'computador':
      return 'Desktop';
    case 'tablet':
      return 'Tablet';
    case 'tv':
      return 'TV';
    default:
      return 'Outros';
  }
}

function mapDeviceType(platform: string): 'mobile' | 'desktop' | 'tablet' | 'tv' | 'other' {
  const platformName = platform.toLowerCase();
  if (platformName.includes('desktop') || platformName.includes('computador')) {
    return 'desktop';
  } else if (platformName.includes('celular') || platformName.includes('mobile')) {
    return 'mobile';
  } else if (platformName.includes('tablet')) {
    return 'tablet';
  } else if (platformName.includes('tv')) {
    return 'tv';
  }
  return 'other';
}

function getDeviceIcon(device: string) {
  const deviceLower = device.toLowerCase();
  if (deviceLower.includes('mobile') || deviceLower.includes('celular')) {
    return <Smartphone className="h-4 w-4 text-[#7E8492]" />;
  } else if (deviceLower.includes('desktop') || deviceLower.includes('computador')) {
    return <Monitor className="h-4 w-4 text-[#7E8492]" />;
  } else if (deviceLower.includes('tablet')) {
    return <TabletIcon className="h-4 w-4 text-[#7E8492]" />;
  } else if (deviceLower.includes('tv')) {
    return <Tv2 className="h-4 w-4 text-[#7E8492]" />;
  }
  return <Monitor className="h-4 w-4 text-[#7E8492]" />;
}

const defaultColumns: MetricColumn[] = [
  { id: 'visualizations', label: 'Visualizações', visible: true },
  { id: 'uniqueViews', label: 'Vis. Únicas', visible: true },
  { id: 'plays', label: 'Plays', visible: true },
  { id: 'uniquePlays', label: 'Plays Únicos', visible: true },
  { id: 'playRate', label: 'Play Rate', visible: true },
  { id: 'buttonClicks', label: 'C. no Botão', visible: false },
  { id: 'engagement', label: 'Engajamento', visible: true },
  { id: 'pitchRetention', label: 'Pitch', visible: false },
  { id: 'conversions', label: 'Conversões', visible: false },
  { id: 'revenue', label: 'Receita', visible: false },
];

type SortField = keyof DeviceMetrics | null;
type SortDirection = 'asc' | 'desc';

export function DevicesMetricsTable({ data = [], pitches = [] }: DevicesMetricsTableProps) {
  useLanguage();
  // Seletor de pitch (só aparece com 2+ pitches): qual pitch preenche a coluna "Pitch".
  const pitchOptions = byPitchOptions(pitches);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);
  const pitchIndex = pitches.findIndex((p) => p.id === pitchId);

  // Mapear dados da API para o formato da tabela
  const deviceMetrics: DeviceMetrics[] = data.map(platform => ({
    device: mapDeviceType(platform.plataforma || ''),
    visualizations: platform.views || 0,
    uniqueViews: platform.uniqueViews || 0,
    plays: platform.plays || 0,
    uniquePlays: platform.uniquePlays || 0,
    playRate: platform.plays && platform.views ? parseFloat(((platform.plays / platform.views) * 100).toFixed(2)) : 0,
    buttonClicks: 0, // TODO: adicionar quando backend enviar
    engagement: parseFloat(platform.engajamento || '0'),
    pitchRetention: pitchIndex >= 0 ? platform.pitchesRetentionPct?.[pitchIndex] ?? null : null,
    conversions: 0, // TODO: adicionar quando backend enviar
    revenue: 0 // TODO: adicionar quando backend enviar
  }));

  const [columns, setColumns] = useState<MetricColumn[]>(defaultColumns);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
  useLanguage();
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-3 w-3 text-[#AEB8C9] group-hover:text-[#6E7A95] transition-colors" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    ) : (
      <ArrowDown className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    );
  };

  const sortedMetrics = [...deviceMetrics].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField] ?? -1;
    const bValue = b[sortField] ?? -1;
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  // Header cell component for consistency
  const HeaderCell = ({ field, label, className = '' }: { field: SortField; label: string; className?: string }) => { useLanguage(); return ((
    <TableHead 
      className={`text-right bg-[#F7F9FF] ${className}`}
      style={{ 
        borderBottom: '1px solid #EDF1FF',
        padding: '10px 12px',
        height: '48px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#5A6475'
      }}
    >
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className="h-auto p-0 hover:bg-transparent font-semibold ml-auto flex text-[#5A6475] hover:text-[#4B7BFF] group transition-colors text-[12px]"
      >
        {label}
        <SortIcon field={field} />
      </Button>
    </TableHead>
  )); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-3">
        {columns.find(c => c.id === 'pitchRetention')?.visible && (
          <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
        )}
        <MetricsColumnFilter columns={columns} onColumnsChange={setColumns} />
      </div>
      
      <Card 
        className="metrics-table-container metrics-table-premium overflow-hidden"
        style={{
          background: '#FFFFFF',
          border: '1px solid #EDF1FF',
          borderRadius: '14px',
          boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.04)',
          marginTop: '32px'
        }}
      >
        <div className="metrics-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow className="border-0">
                <TableHead 
                  className="font-semibold w-[160px] bg-[#F7F9FF]"
                  style={{ 
                    borderBottom: '1px solid #EDF1FF',
                    padding: '10px 12px',
                    height: '48px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#5A6475'
                  }}
                >
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('device')}
                    className="h-auto p-0 hover:bg-transparent font-semibold text-[#5A6475] hover:text-[#4B7BFF] group transition-colors text-[12px]"
                  >
                    {translateInterfaceText("Dispositivo")}<SortIcon field="device" />
                  </Button>
                </TableHead>
                {columns.find(c => c.id === 'visualizations')?.visible && (
                  <HeaderCell field="visualizations" label={translateInterfaceText("Visualizações")} className="min-w-[100px]" />
                )}
                {columns.find(c => c.id === 'uniqueViews')?.visible && (
                  <HeaderCell field="uniqueViews" label={translateInterfaceText("Vis. Únicas")} className="min-w-[100px]" />
                )}
                {columns.find(c => c.id === 'plays')?.visible && (
                  <HeaderCell field="plays" label={translateInterfaceText("Plays")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'uniquePlays')?.visible && (
                  <HeaderCell field="uniquePlays" label={translateInterfaceText("Plays Únicos")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'playRate')?.visible && (
                  <HeaderCell field="playRate" label={translateInterfaceText("Play Rate")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'buttonClicks')?.visible && (
                  <HeaderCell field="buttonClicks" label={translateInterfaceText("C. no Botão")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'engagement')?.visible && (
                  <HeaderCell field="engagement" label={translateInterfaceText("Engajamento")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'pitchRetention')?.visible && (
                  <HeaderCell field="pitchRetention" label={translateInterfaceText("Pitch")} className="min-w-[80px]" />
                )}
                {columns.find(c => c.id === 'conversions')?.visible && (
                  <HeaderCell field="conversions" label={translateInterfaceText("Conversões")} className="min-w-[90px]" />
                )}
                {columns.find(c => c.id === 'revenue')?.visible && (
                  <HeaderCell field="revenue" label={translateInterfaceText("Receita")} className="min-w-[100px]" />
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedMetrics.map((row, index) => (
                <TableRow 
                  key={index}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'} hover:bg-[#F9FAFB] transition-all duration-150 border-b border-[#E5E7EB]`}
                  style={{ height: '44px' }}
                >
                  <TableCell 
                    className="font-semibold text-[#2C3345] text-[12px]"
                    style={{ padding: '8px 12px' }}
                  >
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(row.device)}
                      <span>{getDeviceLabel(row.device)}</span>
                    </div>
                  </TableCell>
                  {columns.find(c => c.id === 'visualizations')?.visible && (
                  <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.visualizations.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniqueViews')?.visible && (
                    <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.uniqueViews.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'plays')?.visible && (
                    <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.plays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniquePlays')?.visible && (
                    <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.uniquePlays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'playRate')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.playRate}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'buttonClicks')?.visible && (
                    <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.buttonClicks.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'engagement')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.engagement}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'pitchRetention')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.pitchRetention != null ? `${row.pitchRetention.toFixed(1)}%` : '—'}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'conversions')?.visible && (
                    <TableCell className="text-right font-medium text-[#0F172A] text-[12px]" style={{ padding: '8px 12px' }}>
                      {row.conversions.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'revenue')?.visible && (
                    <TableCell className="text-right text-[12px]" style={{ padding: '8px 12px' }}>
                      <span className="text-[12px] text-[#0F172A]">R$</span>
                      <span className="text-[13px] font-semibold text-[#0F172A] ml-0.5">
                        {row.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
